import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Badge } from "@circleco/compass/components/Badge";
import type { DataTableColumnDef } from "@circleco/compass/components/DataTable";
import { Select } from "@circleco/compass/components/Select";

import { t } from "@/i18n-js/instance";

import {
  getSpaceGroupMemberRole,
  type RawSpaceGroupMember,
  type SpaceGroupMemberRole,
} from "./helpers";

const ROLE_LABELS: Record<SpaceGroupMemberRole, () => string> = {
  Admin: () => t("spaces.form.edit.members.admin"),
  Moderator: () => t("spaces.form.edit.members.moderator"),
  Member: () => t("spaces.form.edit.members.member"),
};

const getRoleOptions = () => [
  {
    label: t("spaces.form.edit.members.moderator"),
    value: "Moderator" as const,
  },
  { label: t("spaces.form.edit.members.member"), value: "Member" as const },
];

const RoleCell = ({
  row,
  spaceGroupId,
  onRoleChange,
}: {
  row: { original: RawSpaceGroupMember };
  spaceGroupId: number;
  onRoleChange: (memberId: string, role: SpaceGroupMemberRole) => void;
}) => {
  const role = getSpaceGroupMemberRole(row.original, spaceGroupId);
  const { currentCommunityMember } = usePunditUserContext();

  if (role === "Admin" || !isCommunityAdmin(currentCommunityMember)) {
    return <Badge variant="secondary" label={ROLE_LABELS[role]()} />;
  }

  const roleOptions = getRoleOptions();
  const selectedOption = roleOptions.find(o => o.value === role) ?? null;

  return (
    <Select
      options={roleOptions}
      value={selectedOption}
      onValueChange={option => {
        if (option && option.value !== role) {
          onRoleChange(
            String(row.original.id),
            option.value as SpaceGroupMemberRole,
          );
        }
      }}
      aria-label={t(["space_groups.form.edit.members", "role"])}
    />
  );
};

export const getColumns = ({
  onRoleChange,
  spaceGroupId,
}: {
  onRoleChange: (memberId: string, role: SpaceGroupMemberRole) => void;
  spaceGroupId: number;
}): DataTableColumnDef<RawSpaceGroupMember>[] => [
  {
    field: "name",
    get header() {
      return t(["space_groups.form.edit.members", "name"]);
    },
    width: "minmax(180px, 2fr)",
    startContent: (row: RawSpaceGroupMember) => (
      <Avatar src={row.avatar_url ?? undefined} name={row.name} size="sm" />
    ),
    description: (row: RawSpaceGroupMember) => row.email,
  },
  {
    field: "roles",
    get header() {
      return t(["space_groups.form.edit.members", "role"]);
    },
    width: "minmax(140px, 1.5fr)",
    cell: ({ row }: { row: { original: RawSpaceGroupMember } }) => (
      <RoleCell
        row={row}
        spaceGroupId={spaceGroupId}
        onRoleChange={onRoleChange}
      />
    ),
  },
];
